/**
 * Protecao contra duplo envio.
 *
 * Ao submeter um formulario, desabilita os botoes de submit e troca o conteudo
 * por um estado de carregamento com spinner. O rotulo vem de `data-texto-envio`;
 * sem ele, usa "Aguarde…".
 *
 * Por que a aplicacao e adiada por `setTimeout`: o submit pode ser cancelado por
 * outro handler — a confirmacao (`js-confirmar`) ou o jquery-validate-unobtrusive,
 * que chama `preventDefault` quando o formulario e invalido. Nao da para saber
 * isso no meio do dispatch, porque a ordem entre os handlers depende de quem
 * registrou primeiro. Checar `defaultPrevented` depois que o dispatch inteiro
 * terminou e a unica leitura confiavel; se cancelaram, o botao nao trava.
 *
 * `checkValidity` nao substitui esse cuidado: as tag helpers do ASP.NET geram
 * `data-val-*`, nao os atributos nativos, entao um formulario invalido para o
 * jQuery passa como valido para o navegador.
 */

const ATRIBUTO_CARREGANDO = 'data-carregando';
const ATRIBUTO_ENVIANDO = 'data-enviando';

export function iniciarProtecaoDeEnvio(escopo: ParentNode = document): void {
    escopo.querySelectorAll<HTMLFormElement>('form').forEach(formulario => {
        formulario.addEventListener('submit', evento => {
            // Segundo submit do mesmo formulario (Enter no campo, por exemplo,
            // que nao passa pelo botao desabilitado): barra de verdade.
            if (formulario.getAttribute(ATRIBUTO_ENVIANDO) === 'true') {
                evento.preventDefault();
                return;
            }

            if (evento.defaultPrevented) return;
            if (typeof formulario.checkValidity === 'function' && !formulario.checkValidity()) return;

            window.setTimeout(() => {
                if (evento.defaultPrevented) return;

                formulario.setAttribute(ATRIBUTO_ENVIANDO, 'true');
                formulario
                    .querySelectorAll<HTMLButtonElement>('button[type="submit"], button:not([type])')
                    .forEach(aplicarCarregando);
            }, 0);
        });
    });

    // Voltar pelo historico restaura a pagina do bfcache com o botao travado.
    window.addEventListener('pageshow', () => {
        document
            .querySelectorAll<HTMLFormElement>(`form[${ATRIBUTO_ENVIANDO}="true"]`)
            .forEach(formulario => formulario.removeAttribute(ATRIBUTO_ENVIANDO));

        document
            .querySelectorAll<HTMLButtonElement>(`button[${ATRIBUTO_CARREGANDO}="true"]`)
            .forEach(restaurar);
    });
}

function aplicarCarregando(botao: HTMLButtonElement): void {
    if (botao.getAttribute(ATRIBUTO_CARREGANDO) === 'true') return;

    botao.dataset.conteudoOriginal = botao.innerHTML;
    botao.setAttribute(ATRIBUTO_CARREGANDO, 'true');
    botao.setAttribute('aria-busy', 'true');
    botao.disabled = true;

    const rotulo = botao.dataset.textoEnvio ?? 'Aguarde…';

    // O botao do login tem a seta num circulo proprio: o spinner ocupa o lugar
    // dela, em vez de empilhar dois indicadores.
    const seta = botao.querySelector<HTMLElement>('.auth-botao-seta');
    const texto = botao.querySelector<HTMLElement>('.auth-botao-texto');

    if (seta && texto) {
        texto.textContent = rotulo;
        seta.innerHTML = '<span class="cx-spinner" aria-hidden="true"></span>';
        return;
    }

    // Rotulo vazio (botao so de icone numa tabela): o spinner sozinho, sem
    // margem, para nao esticar a celula.
    if (rotulo === '') {
        botao.innerHTML = '<span class="cx-spinner" aria-hidden="true"></span>';
        return;
    }

    botao.innerHTML =
        `<span class="cx-spinner me-2" aria-hidden="true"></span><span>${escapar(rotulo)}</span>`;
}

function restaurar(botao: HTMLButtonElement): void {
    if (botao.dataset.conteudoOriginal !== undefined) {
        botao.innerHTML = botao.dataset.conteudoOriginal;
        delete botao.dataset.conteudoOriginal;
    }
    botao.removeAttribute(ATRIBUTO_CARREGANDO);
    botao.removeAttribute('aria-busy');
    botao.disabled = false;
}

function escapar(valor: string): string {
    const elemento = document.createElement('span');
    elemento.textContent = valor;
    return elemento.innerHTML;
}
